// 🧾 외주업체 거래명세서 — 업체(제조사) 전용 화면 (대표 10-30 "덴클 프로그램도 없는것으로 해서 아예 새로운 파일로, 기존 거래명세서랑 오류가 날까봐")
//   기존 거래명세서 화면(TransactionStatementList·ViewTransactionStatement)은 기공소가 치과에 청구하는 화면이라 쓰지 않는다. 코드도 공유하지 않는다.
//   데이터만 같은 곳: transactionStatements, kind 'outsource_mf'(서버 buildOutsourceMfStatement 가 출고 기준으로 만든 초안).
//   상태는 기존과 같은 3단계(rules 754~): 작성 중(draft) → 발행됨(confirmed, settlementStatus pending) → 정산 완료(settled, 기공소가 기록).
//   업체가 하는 일: [초안 만들기](기공소·달) → 단가·수량 확인 → [저장] / [발행](기공소에 알림) / 작성 중 [삭제].
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import PageHeader from '../common/PageHeader';
import { createNotification } from '../../services/NotificationSystem';
import { buildMfStatementPicked, listReceivedMf, mfMsOf, readVendorPatientName, teethLabel, MF_STATUS, readVendorPriceList, saveVendorPriceList } from '../../services/outsourceMfApi';
import { backfillItems, groupsOf, mergePriceList, tidyServiceName } from '../../utils/mfImplantPrice';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const stateOf = (s) => (s.settlementStatus === 'settled' ? 'settled' : s.status === 'draft' ? 'draft' : 'issued');
const STATE = {
    draft: { text: '작성 중', fg: '#92400e', bg: '#fef3c7' },
    issued: { text: '발행됨', fg: '#1e40af', bg: '#dbeafe' },
    settled: { text: '정산 완료', fg: '#065f46', bg: '#d1fae5' },
};
const monthsBack = (n) => { const out = []; const d = new Date(Date.now() + 9 * 3600 * 1000); for (let i = 0; i < n; i += 1) { out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`); d.setUTCMonth(d.getUTCMonth() - 1); } return out; };
const ymdOf = (ms) => new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 10);   // KST yyyy-mm-dd
const monthStartMs = (delta) => { const d = new Date(Date.now() + 9 * 3600 * 1000); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + delta, 1) - 9 * 3600 * 1000; };
const fmtMd = (ms) => (ms ? `${ymdOf(ms).slice(5).replace('-', '.')}` : '—');
const errText = (e, fb) => (e && e.message && /[가-힣]/.test(e.message) ? e.message : fb);

export default function VendorStatements({ user }) {
    const me = user?.companyId || user?.uid || '';
    const [list, setList] = useState(null);       // 내 외주 명세서
    const [orders, setOrders] = useState([]);     // 받은 외주 의뢰(출고 건 집계용)
    const [loadErr, setLoadErr] = useState('');
    const [from, setFrom] = useState(() => ymdOf(monthStartMs(0)));
    const [to, setTo] = useState(() => ymdOf(Date.now()));
    const [labFilter, setLabFilter] = useState('');      // '' = 전체 기공소
    const [sel, setSel] = useState({});                  // orderId → true
    const [names, setNames] = useState({});              // orderId → 환자 이름(읽을 수 있을 때만)
    const [filterMonth, setFilterMonth] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);         // { ok, text }
    const [openId, setOpenId] = useState('');

    const load = useCallback(async () => {
        if (!me) return;
        setLoadErr('');
        try {
            const [s, o] = await Promise.all([
                getDocs(query(collection(db, 'transactionStatements'), where('fromUserId', '==', me), where('kind', '==', 'outsource_mf'))),
                listReceivedMf(me),
            ]);
            setList(s.docs.map((d) => ({ id: d.id, ...d.data() })).filter((x) => !x.deletedBySender)
                .sort((a, b) => (mfMsOf(b.createdAt) || 0) - (mfMsOf(a.createdAt) || 0)));
            setOrders(o);
        } catch (e) {
            console.error('[외주 명세서] 불러오기 실패 code=%s', e?.code || e?.message);
            setLoadErr('명세서를 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.');
            setList([]);
        }
    }, [me]);
    useEffect(() => { load(); }, [load]);

    // 기공소 목록 = 의뢰를 보낸 곳
    const labs = useMemo(() => {
        const m = {};
        orders.forEach((o) => { if (o.fromUserId) m[o.fromUserId] = o.fromName || m[o.fromUserId] || '기공소'; });
        return Object.entries(m).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    }, [orders]);
    // 이미 명세서에 들어간 의뢰 → 그 명세서 번호(지운 명세서 제외)
    const billed = useMemo(() => {
        const m = {};
        (list || []).forEach((st) => (st.outsourceMfOrderIds || []).forEach((id) => { m[id] = st.statementNumber || st.id.slice(0, 8); }));
        return m;
    }, [list]);
    // 받은 의뢰서 — 기간(받은 날)·기공소로 거름. 10-31 오후부터 출고 전 건도 고를 수 있다(거절·취소 제외)
    const fromMs = Date.parse(`${from}T00:00:00+09:00`), toMs = Date.parse(`${to}T23:59:59+09:00`);
    const rowsInPeriod = useMemo(() => orders
        .filter((o) => (!labFilter || o.fromUserId === labFilter))
        .filter((o) => { const t = mfMsOf(o.createdAt) || 0; return t >= fromMs && t <= toMs; })
        .sort((a, b) => (mfMsOf(b.createdAt) || 0) - (mfMsOf(a.createdAt) || 0)), [orders, labFilter, fromMs, toMs]);
    // 고르기: 거절·취소·이미 명세서에 들어간 건만 막는다(대표 10-31 "선택이 안되고 전체 선택이 되게해" — 출고 전 건도 고를 수 있게)
    const canPick = (o) => !['rejected', 'cancelled'].includes(o.status) && !billed[o.id];
    const picked = rowsInPeriod.filter((o) => sel[o.id] && canPick(o));
    const pickedLabs = [...new Set(picked.map((o) => o.fromUserId))];
    const pickable = rowsInPeriod.filter(canPick);
    const allOn = pickable.length > 0 && pickable.every((o) => sel[o.id]);
    const toggleAll = () => { const m = {}; if (!allOn) pickable.forEach((o) => { m[o.id] = true; }); setSel(m); };
    // 기간·기공소가 바뀌면 출고 건만 고른 상태로 시작(출고 전 건은 직접 고른다)
    useEffect(() => { const m = {}; rowsInPeriod.forEach((o) => { if (canPick(o) && o.status === 'shipped') m[o.id] = true; }); setSel(m); }, [rowsInPeriod, billed]); // eslint-disable-line react-hooks/exhaustive-deps
    // 환자 이름 — 보이는 줄만(최대 100), 업체가 읽을 수 있는 동안만(기한 지나면 빈칸)
    useEffect(() => {
        let on = true;
        const need = rowsInPeriod.slice(0, 100).filter((o) => !(o.id in names));
        if (!need.length) return undefined;
        Promise.all(need.map((o) => readVendorPatientName(o.id).then((n) => [o.id, n || ''], () => [o.id, ''])))
            .then((pairs) => { if (on) setNames((p) => ({ ...p, ...Object.fromEntries(pairs) })); });
        return () => { on = false; };
    }, [rowsInPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

    const makeDraft = async () => {
        if (busy || !picked.length) return;
        setBusy(true); setMsg(null);
        const made = []; const fails = [];
        for (const lid of pickedLabs) {   // 기공소마다 한 장
            const ids = picked.filter((o) => o.fromUserId === lid).map((o) => o.id);
            try { const r = await buildMfStatementPicked(lid, ids); made.push(r); }
            catch (e) { console.error('[외주 명세서] 초안 실패 code=%s', e?.code); fails.push(`${nameOfLab(lid)}: ${errText(e, '만들지 못했습니다')}`); }
        }
        await load();
        if (made.length === 1) setOpenId(made[0].statementId);
        setMsg(fails.length ? { ok: made.length > 0, text: `${made.length}장 만듦 · 실패 ${fails.length} — ${fails[0]}` }
            : { ok: true, text: `명세서 초안 ${made.length}장을 만들었습니다(${picked.length}건). 아래 목록에서 단가를 확인하고 발행하세요.` });
        setBusy(false);
    };

    const shown = (list || []).filter((s) => !filterMonth || s.outsourceMfMonth === filterMonth);
    const open = (list || []).find((s) => s.id === openId) || null;
    const nameOfLab = (id, fb) => (labs.find((l) => l.id === id) || {}).name || fb || '기공소';

    return (
        <div style={S.page}>
            <PageHeader title="외주 거래명세서" />
            <div style={S.intro}>{'기공소에 보낼 외주 명세서입니다. 기간을 골라 받은 의뢰서를 보고, 넣을 건을 골라 명세서를 만듭니다.'}</div>

            <section style={S.card}>
                <div style={S.h2}>{'받은 의뢰서'}</div>
                <div style={S.formRow}>
                    <label style={{ ...S.field, flex: '1 1 150px' }}><span style={S.lbl}>{'시작일 (받은 날)'}</span><input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={S.input} /></label>
                    <label style={{ ...S.field, flex: '1 1 150px' }}><span style={S.lbl}>{'종료일'}</span><input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={S.input} /></label>
                    <label style={{ ...S.field, flex: '1 1 200px' }}><span style={S.lbl}>{'기공소'}</span>
                        <select value={labFilter} onChange={(e) => setLabFilter(e.target.value)} style={S.input}><option value="">{'전체 기공소'}</option>{labs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
                    </label>
                    <div style={{ ...S.field, flex: '2 1 260px', justifyContent: 'flex-end' }}>
                        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button type="button" style={S.ghost} onClick={() => { setFrom(ymdOf(monthStartMs(0))); setTo(ymdOf(Date.now())); }}>{'이번 달'}</button>
                            <button type="button" style={S.ghost} onClick={() => { setFrom(ymdOf(monthStartMs(-1))); setTo(ymdOf(monthStartMs(0) - 1)); }}>{'지난 달'}</button>
                            <button type="button" style={S.ghost} onClick={() => { setFrom(ymdOf(monthStartMs(-2))); setTo(ymdOf(Date.now())); }}>{'최근 3개월'}</button>
                        </span>
                    </div>
                </div>
                <div style={{ ...S.scroll }}>
                    <table style={{ ...S.table, minWidth: 860 }}>
                        <thead><tr style={S.thr}><th style={{ ...S.th, ...S.stick, width: 32 }}><input type="checkbox" checked={allOn} disabled={!pickable.length} onChange={toggleAll} aria-label="전체 선택" title="전체 선택" style={{ width: 17, height: 17 }} /></th><th style={{ ...S.th, ...S.stick }}>{'받은 날'}</th><th style={{ ...S.th, ...S.stick }}>{'기공소'}</th><th style={{ ...S.th, ...S.stick }}>{'환자'}</th><th style={{ ...S.th, ...S.stick }}>{'치아'}</th><th style={{ ...S.th, ...S.stick }}>{'품목'}</th><th style={{ ...S.th, ...S.stick }}>{'어버트먼트 종류'}</th><th style={{ ...S.th, ...S.stick }}>{'상태'}</th><th style={{ ...S.th, ...S.stick }}>{'명세서'}</th></tr></thead>
                        <tbody>
                            {rowsInPeriod.map((o) => {
                                const st = MF_STATUS[o.status] || MF_STATUS.pending;
                                const ok = canPick(o);
                                return (
                                    <tr key={o.id} style={{ borderTop: '1px solid #f1f5f9', background: sel[o.id] && ok ? '#eef2ff' : undefined }}>
                                        <td style={S.td}><input type="checkbox" checked={!!sel[o.id] && ok} disabled={!ok} onChange={() => setSel((m) => ({ ...m, [o.id]: !m[o.id] }))} title={o.caseNo} aria-label="명세서에 넣기" style={{ width: 17, height: 17 }} /></td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtMd(mfMsOf(o.createdAt))}</td>
                                        <td style={S.td}>{o.fromName || nameOfLab(o.fromUserId)}</td>
                                        <td style={S.td}>{names[o.id] || <span style={S.dim}>{'—'}</span>}</td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{teethLabel(o.teeth, o.bridge) || '—'}</td>
                                        <td style={S.td}>{(o.detail && o.detail.item) || '—'}</td>
                                        <td style={S.td}>{(o.detail && o.detail.system) || <span style={S.dim}>{'—'}</span>}{o.detail && o.detail.platform ? <div style={{ ...S.dim, fontSize: 12 }}>{o.detail.platform}</div> : null}</td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}><span style={{ ...S.pill, color: st.fg, background: st.bg }}>{o.status === 'pending' ? (st.recvText || st.text) : st.text}</span>{o.status === 'shipped' && o.shippedAt ? <span style={{ ...S.dim, marginLeft: 6 }}>{fmtMd(mfMsOf(o.shippedAt))}</span> : null}</td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{billed[o.id] ? <span style={S.dim}>{billed[o.id]}</span> : ['rejected', 'cancelled'].includes(o.status) ? <span style={{ ...S.dim, color: '#b91c1c' }} title={o.rejectReason || o.cancelReason || ''}>{'제외'}</span> : <span style={{ ...S.dim, color: '#4338ca' }}>{'넣을 수 있음'}</span>}</td>
                                    </tr>
                                );
                            })}
                            {!rowsInPeriod.length && <tr><td colSpan={9} style={{ ...S.td, ...S.dim, textAlign: 'center', padding: 24 }}>{list === null ? '불러오는 중…' : '이 기간에 받은 의뢰가 없습니다.'}</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={S.dim}>{`받은 의뢰 ${rowsInPeriod.length}건 · 고를 수 있는 건 ${pickable.length}건 (거절·취소·이미 명세서에 들어간 건 제외)`}</span>
                    <span style={{ flex: 1 }} />
                    <button type="button" onClick={makeDraft} disabled={busy || !picked.length} style={busy || !picked.length ? S.btnOff : S.btn}>
                        {busy ? '만드는 중…' : picked.length ? `고른 ${picked.length}건으로 명세서 만들기${pickedLabs.length > 1 ? ` · 기공소별 ${pickedLabs.length}장` : ''}` : '의뢰를 고르세요'}
                    </button>
                </div>
                {msg && <div style={msg.ok ? S.ok : S.err}>{msg.text}</div>}
            </section>

            <section style={S.card}>
                <div style={{ ...S.h2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ flex: 1 }}>{'내 외주 명세서'}</span>
                    <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={{ ...S.input, width: 'auto', fontWeight: 400 }}>
                        <option value="">{'전체 달'}</option>
                        {monthsBack(12).map((m) => <option key={m} value={m}>{m.replace('-', '년 ')}월</option>)}
                    </select>
                    <button type="button" onClick={load} style={S.ghost}>{'새로고침'}</button>
                </div>
                {loadErr && <div style={S.err}>{loadErr}</div>}
                {list === null ? <div style={S.dim}>{'불러오는 중…'}</div> : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={S.table}>
                            <thead><tr style={S.thr}><th style={S.th}>{'번호'}</th><th style={S.th}>{'기공소'}</th><th style={S.th}>{'달'}</th><th style={{ ...S.th, textAlign: 'right' }}>{'건수'}</th><th style={{ ...S.th, textAlign: 'right' }}>{'합계'}</th><th style={S.th}>{'상태'}</th></tr></thead>
                            <tbody>
                                {shown.map((s) => {
                                    const st = STATE[stateOf(s)];
                                    const needs = (s.items || []).filter((i) => i.needsPriceCheck && !(Number(i.unitPrice) > 0)).length;
                                    return (
                                        <tr key={s.id} style={{ ...S.tr, background: openId === s.id ? '#eef2ff' : undefined }} onClick={() => setOpenId(s.id)}>
                                            <td style={{ ...S.td, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 }}>{s.statementNumber || s.id.slice(0, 8)}</td>
                                            <td style={S.td}>{s.toUserName || nameOfLab(s.toUserId)}</td>
                                            <td style={S.td}>{(s.outsourceMfMonth || '').replace('-', '년 ')}{s.outsourceMfMonth ? '월' : ''}</td>
                                            <td style={{ ...S.td, textAlign: 'right' }}>{(s.items || []).length}</td>
                                            <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{won(s.totalAmount)}</td>
                                            <td style={S.td}><span style={{ ...S.pill, color: st.fg, background: st.bg }}>{st.text}</span>{needs > 0 && s.status === 'draft' && <span style={{ ...S.pill, color: '#b45309', background: '#fff7ed', marginLeft: 6 }}>{`단가 확인 ${needs}`}</span>}</td>
                                        </tr>
                                    );
                                })}
                                {!shown.length && <tr><td colSpan={6} style={{ ...S.td, ...S.dim, textAlign: 'center', padding: 24 }}>{'명세서가 없습니다. 위에서 기공소와 달을 고르고 [초안 만들기]를 누르세요.'}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {open && <StatementDetail key={open.id} s={open} orders={orders} vendorUid={user?.uid || ''} labName={open.toUserName || nameOfLab(open.toUserId)} onClose={() => setOpenId('')} onChanged={load} />}
        </div>
    );
}

/** 명세서 한 장 — 작성 중이면 단가·수량·메모 수정, [저장]·[발행]·[삭제]. 발행 뒤엔 보기만 */
function StatementDetail({ s, orders, vendorUid, labName, onClose, onChanged }) {
    const editable = s.status === 'draft';
    // 11-02: 그 전에 만든 초안은 줄에 임플란트 종류가 없어 받은 의뢰서에서 채운다(작성 중일 때만 — 발행본은 그대로)
    const [items, setItems] = useState(() => {
        const its = (s.items || []).map((i) => ({ ...i }));
        return editable ? backfillItems(its, s.outsourceMfOrderIds, orders).map((i) => ({ ...i, serviceName: tidyServiceName(i.serviceName) })) : its;   // 11-02 중복 단어 정리(작성 중만)
    });
    // 임플란트 종류별 단가 한번에(대표 11-02) — 묶음별 입력값, 단가표에도 저장할지
    const [groupPrice, setGroupPrice] = useState({});
    const [toPriceList, setToPriceList] = useState(true);
    const groups = useMemo(() => groupsOf(items), [items]);
    const [notes, setNotes] = useState(s.notes || '');
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);
    const [confirmIssue, setConfirmIssue] = useState(false);
    const [confirmDel, setConfirmDel] = useState(false);

    const lineTotal = (i) => (Number(i.unitPrice) || 0) * (Number(i.toothCount) || 0);
    const total = items.reduce((a, i) => a + lineTotal(i), 0);
    const missing = items.filter((i) => !(Number(i.unitPrice) > 0)).length;
    const setItem = (idx, k, v) => setItems((xs) => xs.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));

    const payload = () => {
        const its = items.map((i) => {
            const unit = Number(i.unitPrice) || 0; const qty = Math.max(0, Math.round(Number(i.toothCount) || 0));
            return { ...i, unitPrice: unit, pricePerTooth: unit, toothCount: qty, totalPrice: unit * qty, price: unit * qty, needsPriceCheck: !(unit > 0) };
        });
        const sum = its.reduce((a, i) => a + i.totalPrice, 0);
        return { items: its, subtotal: sum, totalAmount: sum, discountAmount: 0, totalDiscount: 0, totalTeeth: its.reduce((a, i) => a + i.toothCount, 0), notes: notes.slice(0, 1000), updatedAt: new Date() };
    };
    const save = async () => {
        setBusy('save'); setMsg(null);
        try { await updateDoc(doc(db, 'transactionStatements', s.id), payload()); setMsg({ ok: true, text: '저장했습니다.' }); onChanged(); }
        catch (e) { console.error('[외주 명세서] 저장 실패 code=%s', e?.code); setMsg({ ok: false, text: '저장하지 못했습니다.' }); }
        finally { setBusy(''); }
    };
    const issue = async () => {
        setBusy('issue'); setMsg(null);
        try {
            await updateDoc(doc(db, 'transactionStatements', s.id), {
                ...payload(), status: 'confirmed', publishedAt: new Date(),
                settlementStatus: 'pending', settlementNotes: [], settledAt: null, settledBy: null, settledByName: null,
            });
            try {
                await createNotification({ recipientId: s.toUserId, type: 'statement_received', title: '외주 거래명세서 수신',
                    message: `${s.fromUserName || '외주업체'}에서 ${(s.outsourceMfMonth || '').replace('-', '년 ')}월 외주 명세서를 발행했습니다.${s.statementNumber ? ` (${s.statementNumber})` : ''}`, link: '/transactions' });
            } catch (e) { console.error('[외주 명세서] 발행 알림 실패 code=%s', e?.code || e?.message); }
            setConfirmIssue(false);
            setMsg({ ok: true, text: '발행했습니다. 기공소에 알림이 갔습니다.' });
            onChanged();
        } catch (e) { console.error('[외주 명세서] 발행 실패 code=%s', e?.code); setMsg({ ok: false, text: '발행하지 못했습니다.' }); }
        finally { setBusy(''); }
    };
    const applyGroups = async () => {
        const picked = groups.map((g) => ({ g, price: Math.round(Number(groupPrice[g.key])) })).filter((x) => x.price > 0);
        if (!picked.length) { setMsg({ ok: false, text: '넣을 단가를 하나 이상 적어 주세요.' }); return; }
        setItems((xs) => xs.map((x, i) => { const hit = picked.find((p) => p.g.idxs.includes(i)); return hit ? { ...x, unitPrice: hit.price } : x; }));
        setGroupPrice({});
        const filled = picked.reduce((a, p) => a + p.g.idxs.length, 0);
        if (!toPriceList) { setMsg({ ok: true, text: `${filled}줄에 단가를 넣었습니다. [저장]을 눌러야 명세서에 남습니다.` }); return; }
        setBusy('price'); setMsg(null);
        try {
            const cur = await readVendorPriceList(vendorUid);
            if (cur === null) throw Object.assign(new Error('no-access'), { code: 'permission-denied' });
            const r = mergePriceList(cur, picked.map(({ g, price }) => ({ kind: g.kind, item: g.item, material: g.material, system: g.system, price })));
            if (r.added || r.updated) await saveVendorPriceList(vendorUid, r.items);
            const skip = r.skipped ? ` (단가표에 못 넣은 것 ${r.skipped}개 — 품목 정보가 없거나 단가표가 200줄로 가득 참)` : '';
            setMsg({ ok: !r.skipped, text: `${filled}줄에 단가를 넣고, 단가표에 새로 ${r.added}개·바꿈 ${r.updated}개 저장했습니다.${skip} [저장]을 눌러야 명세서에 남습니다.` });
        } catch (e) {
            console.error('[외주 명세서] 단가표 저장 실패 code=%s', e?.code || e?.message);
            setMsg({ ok: false, text: `${filled}줄에 단가는 넣었지만 단가표에는 저장하지 못했습니다${e?.code === 'permission-denied' ? '(업체 대표 계정만 단가표를 바꿀 수 있습니다)' : ''}. [저장]을 눌러야 명세서에 남습니다.` });
        } finally { setBusy(''); }
    };
    const remove = async () => {
        setBusy('del'); setMsg(null);
        try { await deleteDoc(doc(db, 'transactionStatements', s.id)); onClose(); onChanged(); }
        catch (e) { console.error('[외주 명세서] 삭제 실패 code=%s', e?.code); setMsg({ ok: false, text: '삭제하지 못했습니다.' }); setBusy(''); }
    };
    const st = STATE[stateOf(s)];

    return (
        <section style={{ ...S.card, borderColor: '#c7d2fe' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 16 }}>{`${labName} · ${(s.outsourceMfMonth || '').replace('-', '년 ')}월`}</b>
                <span style={{ ...S.pill, color: st.fg, background: st.bg }}>{st.text}</span>
                <span style={{ ...S.dim, fontFamily: 'ui-monospace, Menlo, monospace' }}>{s.statementNumber}</span>
                <span style={{ flex: 1 }} />
                <button type="button" onClick={onClose} style={S.ghost}>{'닫기'}</button>
            </div>
            {editable && groups.length > 0 && (
                <div style={S.groupBox}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{'임플란트 종류별 단가 — 한번에 넣기'}</div>
                    <div style={S.dim}>{'같은 품목·임플란트 종류 줄에 같은 단가가 한꺼번에 들어갑니다. 비워 둔 칸은 바꾸지 않습니다.'}</div>
                    {groups.map((g) => (
                        <div key={g.key} style={S.groupRow}>
                            <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                                <b>{g.system || g.label}</b>
                                <span style={S.dim}>{`${g.system ? ` · ${g.label}` : ''}${g.material ? ` · ${g.material}` : ''} · ${g.idxs.length}건 ${g.qty}개${g.price ? ` · 지금 ${won(g.price)}` : ''}`}</span>
                            </span>
                            <input type="number" min={0} step={1000} placeholder={g.price ? String(g.price) : '단가'} value={groupPrice[g.key] || ''}
                                onChange={(e) => setGroupPrice((p) => ({ ...p, [g.key]: e.target.value }))} style={{ ...S.num, width: 120 }} aria-label={`${g.system || g.label} 단가`} />
                        </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <input type="checkbox" checked={toPriceList} onChange={(e) => setToPriceList(e.target.checked)} />
                            {'단가표에도 저장 (다음 명세서부터 자동으로 채움)'}
                        </label>
                        <span style={{ flex: 1 }} />
                        <button type="button" onClick={applyGroups} disabled={!!busy} style={S.ghost}>{busy === 'price' ? '넣는 중…' : '한번에 넣기'}</button>
                    </div>
                </div>
            )}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ ...S.table, minWidth: 760 }}>
                    <thead><tr style={S.thr}><th style={S.th}>{'케이스'}</th><th style={S.th}>{'환자'}</th><th style={S.th}>{'치아'}</th><th style={S.th}>{'품목'}</th><th style={{ ...S.th, textAlign: 'right' }}>{'수량'}</th><th style={{ ...S.th, textAlign: 'right' }}>{'단가'}</th><th style={{ ...S.th, textAlign: 'right' }}>{'금액'}</th></tr></thead>
                    <tbody>
                        {items.map((i, idx) => (
                            <tr key={`${i.caseNo}_${idx}`} style={S.tr}>
                                <td style={{ ...S.td, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, whiteSpace: 'nowrap' }}>{i.caseNo || '—'}</td>
                                <td style={S.td}>{i.patientName || <span style={S.dim}>{'—'}</span>}</td>
                                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{i.toothInfo || '—'}</td>
                                <td style={S.td}>{tidyServiceName(i.serviceName)}{i.material ? <span style={S.dim}>{` · ${i.material}`}</span> : null}</td>
                                <td style={{ ...S.td, textAlign: 'right' }}>{editable ? <input type="number" min={0} value={i.toothCount} onChange={(e) => setItem(idx, 'toothCount', e.target.value)} style={{ ...S.num, width: 64 }} aria-label="수량" /> : i.toothCount}</td>
                                <td style={{ ...S.td, textAlign: 'right' }}>{editable ? <input type="number" min={0} step={1000} value={i.unitPrice === '' ? '' : i.unitPrice} placeholder="단가" onChange={(e) => setItem(idx, 'unitPrice', e.target.value)} style={{ ...S.num, width: 110, borderColor: Number(i.unitPrice) > 0 ? '#cbd5e1' : '#f59e0b' }} aria-label="단가" /> : won(i.unitPrice)}</td>
                                <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{won(lineTotal(i))}</td>
                            </tr>
                        ))}
                        <tr><td colSpan={6} style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{'합계'}</td><td style={{ ...S.td, textAlign: 'right', fontWeight: 800, fontSize: 15 }}>{won(total)}</td></tr>
                    </tbody>
                </table>
            </div>
            <label style={{ ...S.lbl, marginTop: 4 }} htmlFor="vstmt-notes">{'메모 (기공소에 보임)'}</label>
            {editable
                ? <textarea id="vstmt-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...S.input, resize: 'vertical' }} />
                : <div style={{ fontSize: 13.5, color: '#334155', whiteSpace: 'pre-wrap' }}>{s.notes || '—'}</div>}
            {editable && missing > 0 && <div style={S.note}>{`단가가 비어 있는 줄이 ${missing}개 있습니다. 위 「임플란트 종류별 단가」에서 한번에 넣고 단가표에도 저장하면 다음부터 자동으로 채워집니다.`}</div>}
            {!editable && <div style={S.dim}>{stateOf(s) === 'settled' ? '기공소가 정산 완료로 기록했습니다.' : '발행했습니다. 기공소가 받은 명세서에서 확인·정산합니다.'}</div>}
            {msg && <div style={msg.ok ? S.ok : S.err}>{msg.text}</div>}
            {editable && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {confirmDel
                        ? <><span style={{ ...S.err, alignSelf: 'center' }}>{'이 초안을 지울까요?'}</span><button type="button" onClick={remove} disabled={!!busy} style={S.danger}>{busy === 'del' ? '지우는 중…' : '지우기'}</button><button type="button" onClick={() => setConfirmDel(false)} style={S.ghost}>{'아니요'}</button></>
                        : <button type="button" onClick={() => setConfirmDel(true)} disabled={!!busy} style={S.ghost}>{'초안 삭제'}</button>}
                    <span style={{ flex: 1 }} />
                    <button type="button" onClick={save} disabled={!!busy} style={S.ghost}>{busy === 'save' ? '저장 중…' : '저장'}</button>
                    {confirmIssue
                        ? <><span style={{ alignSelf: 'center', fontSize: 13 }}>{`${won(total)}으로 ${labName}에 발행할까요? 발행 뒤에는 금액을 고칠 수 없습니다.`}</span><button type="button" onClick={issue} disabled={!!busy} style={S.btn}>{busy === 'issue' ? '발행 중…' : '발행'}</button><button type="button" onClick={() => setConfirmIssue(false)} style={S.ghost}>{'아니요'}</button></>
                        : <button type="button" onClick={() => setConfirmIssue(true)} disabled={!!busy || missing > 0} style={missing > 0 ? S.btnOff : S.btn} title={missing > 0 ? '단가를 모두 넣어야 발행할 수 있습니다' : ''}>{'발행'}</button>}
                </div>
            )}
        </section>
    );
}

const S = {
    page: { padding: 24, maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 },
    intro: { fontSize: 13.5, color: '#475569' },
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
    h2: { fontWeight: 700, fontSize: 15, color: '#0f172a' },
    formRow: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' },
    field: { display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px', minWidth: 0 },
    lbl: { fontSize: 12.5, fontWeight: 600, color: '#334155' },
    input: { width: '100%', boxSizing: 'border-box', padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, background: '#fff' },
    num: { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13.5, textAlign: 'right', boxSizing: 'border-box' },
    btn: { border: 0, borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 700, background: '#4f46e5', color: '#fff', cursor: 'pointer' },
    btnOff: { border: 0, borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 700, background: '#cbd5e1', color: '#fff', cursor: 'not-allowed' },
    ghost: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 12px', fontSize: 13, background: '#fff', color: '#334155', cursor: 'pointer' },
    danger: { border: 0, borderRadius: 8, padding: '8px 12px', fontSize: 13, background: '#dc2626', color: '#fff', cursor: 'pointer' },
    dim: { fontSize: 12.5, color: '#64748b' },
    note: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '8px 10px' },
    ok: { fontSize: 13, color: '#065f46', background: '#ecfdf5', borderRadius: 8, padding: '8px 10px' },
    err: { fontSize: 13, color: '#b91c1c' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 620 },
    thr: { background: '#f8fafc', color: '#334155', textAlign: 'left' },
    th: { padding: '9px 10px', fontWeight: 600 },
    stick: { position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 },
    scroll: { overflow: 'auto', maxHeight: 520, border: '1px solid #f1f5f9', borderRadius: 8 },
    tr: { borderTop: '1px solid #f1f5f9', cursor: 'pointer' },
    td: { padding: '9px 10px', verticalAlign: 'middle' },
    groupBox: { border: '1px solid #e0e7ff', background: '#f8faff', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 },
    groupRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid #eef2ff', fontSize: 13.5 },
    pill: { display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700 },
};
